import {
  AuthType,
  IConnectionInput,
  StrategyKind,
} from '../../../../utils/apollo/graphqlSchema';
import { z } from 'zod';

const CDW_STRATEGY_NAMES: Record<StrategyKind, string> = {
  [StrategyKind.Snowflake]: 'Snowflake',
  [StrategyKind.Bigquery]: 'BigQuery',
  [StrategyKind.Clickhouse]: 'ClickHouse',
  [StrategyKind.Databricks]: 'Databricks',
  [StrategyKind.Mysql]: 'MySQL',
  [StrategyKind.Presto]: 'Presto',
  [StrategyKind.Redshift]: 'Redshift',
};

export const CDW_STRATEGY_OPTS = Object.values(StrategyKind).map((strategy) => {
  return { text: CDW_STRATEGY_NAMES[strategy], value: strategy };
});

type StrategyAuthConfig = {
  [K in StrategyKind]?: ReadonlyArray<AuthType>;
};

const STRATEGY_AUTH_OPTIONS: StrategyAuthConfig = {
  [StrategyKind.Snowflake]: [AuthType.UserPassword, AuthType.UserPrivateKey],
  [StrategyKind.Bigquery]: [AuthType.UserPassword],
  [StrategyKind.Clickhouse]: [AuthType.UserPassword],
  [StrategyKind.Databricks]: [AuthType.UserPassword],
  [StrategyKind.Mysql]: [AuthType.UserPassword],
  [StrategyKind.Presto]: [AuthType.UserPassword],
  [StrategyKind.Redshift]: [AuthType.UserPassword],
};

const AUTH_TYPE_LABELS = {
  [AuthType.UserPassword]: 'User and Password',
  [AuthType.UserPrivateKey]: 'Key-pair Authentication',
};

interface AuthOption {
  text: string;
  value: AuthType;
}

export const getAuthOptions = (strategy: StrategyKind): AuthOption[] => {
  const availableOptions = STRATEGY_AUTH_OPTIONS[strategy] || [];

  return availableOptions.map((authType) => ({
    text: AUTH_TYPE_LABELS[authType],
    value: authType,
  }));
};

const validTimeout = z.coerce.number().positive().int();

export const parseTimeout = (s: string) => {
  return validTimeout.safeParse(s).data ?? null;
};

const isValidBqJson = (keyStr: string): boolean => {
  try {
    const parsed = JSON.parse(keyStr);
    return typeof parsed === 'object' && !!parsed.private_key;
  } catch (e) {
    return false;
  }
};

export const canEditConnectionDetails = (
  isNewSchema: boolean,
  readOnly: boolean,
  connectionInput: IConnectionInput,
): boolean => {
  if (readOnly) {
    return false;
  }
  if (isNewSchema) {
    return true;
  }
  return (
    (!!connectionInput.bigQuery &&
      isValidBqJson(connectionInput.bigQuery.keyPlain)) ||
    (!!connectionInput.basic &&
      !!connectionInput.basic.password &&
      connectionInput.basic.password.trim().length > 0) ||
    (!!connectionInput.basic && !!connectionInput.basic.privateKey)
  );
};
